<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Canvas from "./Canvas.client.vue";

const route = useRoute();
const router = useRouter();
const companionStore = useCompanion();

const activeSubmissionId = computed(() => {
  return (route.params.documentId as string) || null;
});

const isSidebarOpen = ref(!activeSubmissionId.value);

// Watch for documentId changes to manage sidebar state
watch(
  () => activeSubmissionId.value,
  (newId) => {
    if (newId) {
      isSidebarOpen.value = false;
    } else {
      isSidebarOpen.value = true;
    }
  },
  { immediate: true }
);

const handleSelectSubmission = (submission: any) => {
  router.push(useCreateCompanionPortalRoute(`/${submission.id}`));
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const handleNewSubmission = () => {
  router.push(useCreateCompanionPortalRoute("/"));
};

onMounted(async () => {
  await useAppUserDataStore().initializeAppUserData();
  await companionStore.initializeProfiles();
});
</script>

<template>
  <div class="flex h-full overflow-hidden bg-gray-50 relative font-sans">
    <CompanionProfileGate />

    <!-- Main Content Area -->
    <div
      class="flex-1 flex flex-col relative overflow-hidden transition-all duration-300 bg-gray-50/50"
    >
      <!-- Welcome Screen (No active submission) -->
      <div v-if="!activeSubmissionId" class="">
        <CompanionCreateSubmissionContainer />
      </div>

      <!-- Editor Canvas (Active submission) -->
      <div v-else class="flex-1 h-full overflow-hidden relative">
        <Canvas
          :document-id="activeSubmissionId as string"
          @toggle-sidebar="toggleSidebar"
        />
      </div>
    </div>

    <!-- Sidebar -->
    <CompanionSidebarContainer
      v-if="activeSubmissionId != null"
      :is-open="activeSubmissionId != null && isSidebarOpen"
      :active-submission-id="activeSubmissionId ?? undefined"
      @toggle="toggleSidebar"
      @select="handleSelectSubmission"
      @new-submission="handleNewSubmission"
      class="border-r border-gray-200 shadow-sm transition-all duration-300 absolute top-0 right-0 h-full !w-[350px] z-[99999]"
      :class="{
        'right-[-350px]': isSidebarOpen != true,
        'right-0': isSidebarOpen == true,
      }"
    />

    <!-- Backdrop for overlay mode -->
    <div
      v-if="activeSubmissionId && isSidebarOpen"
      class="absolute inset-0 bg-gray-900/20 z-20 backdrop-blur-[1px] transition-opacity"
      @click="toggleSidebar"
    ></div>
  </div>
</template>
